/* aminos-amino-explorer — tabbed slider built on the theme's native Swiper.
   Tabs sync with the slider: click a tab to go to that slide; swiping or the
   arrows updates the active tab (and scrolls it into view). */
if (!customElements.get('aminos-amino-explorer')) {
  customElements.define(
    'aminos-amino-explorer',
    class AminosAminoExplorer extends HTMLElement {
      connectedCallback() {
        this.swiperEl = this.querySelector('.swiper');
        this.tabsEl = this.querySelector('.aminos-amino-explorer__tabs');
        this.tabs = Array.from(this.querySelectorAll('.aminos-amino-explorer__tab'));
        if (!this.swiperEl) return;
        this.init();
      }

      init() {
        if (!window.FoxTheme || !window.FoxTheme.Carousel || !window.FoxTheme.Swiper) {
          window.requestAnimationFrame(() => this.init());
          return;
        }

        this.carousel = new window.FoxTheme.Carousel(this.swiperEl, {
          slidesPerView: 1.1,
          spaceBetween: 16,
          centeredSlides: false,
          loop: false,
          autoHeight: false,
          speed: 400,
          threshold: 2,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          on: {
            afterInit: (swiper) => this.setActive(swiper.activeIndex),
            slideChange: (swiper) => this.setActive(swiper.activeIndex),
          },
        });
        this.carousel.init();

        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => {
            if (this.carousel && this.carousel.slider) this.carousel.slider.slideTo(i);
          });
        });

        this.bindTabScroll();
      }

      // Make the tab strip scrollable by dragging (touch + mouse) and by mouse
      // wheel. Native touch scrolling can be unreliable inside the slider
      // section, so we drive horizontal scroll directly with pointer events.
      bindTabScroll() {
        const tabs = this.tabsEl;
        if (!tabs) return;

        // Mouse wheel → horizontal (desktop), only when it overflows.
        tabs.addEventListener(
          'wheel',
          (e) => {
            if (tabs.scrollWidth <= tabs.clientWidth) return;
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
              tabs.scrollLeft += e.deltaY;
              e.preventDefault();
            }
          },
          { passive: false }
        );

        // Pointer drag → horizontal scroll.
        let down = false;
        let startX = 0;
        let startScroll = 0;
        let dragged = false;

        tabs.addEventListener('pointerdown', (e) => {
          down = true;
          dragged = false;
          startX = e.clientX;
          startScroll = tabs.scrollLeft;
        });

        tabs.addEventListener('pointermove', (e) => {
          if (!down) return;
          const dx = e.clientX - startX;
          if (!dragged && Math.abs(dx) > 5) {
            dragged = true;
            tabs.classList.add('is-dragging');
            try {
              tabs.setPointerCapture(e.pointerId);
            } catch (_) {}
          }
          if (dragged) {
            tabs.scrollLeft = startScroll - dx;
            if (e.cancelable) e.preventDefault();
          }
        });

        const release = (e) => {
          down = false;
          tabs.classList.remove('is-dragging');
          if (e && e.pointerId != null && tabs.hasPointerCapture && tabs.hasPointerCapture(e.pointerId)) {
            try {
              tabs.releasePointerCapture(e.pointerId);
            } catch (_) {}
          }
        };
        tabs.addEventListener('pointerup', release);
        tabs.addEventListener('pointercancel', release);
        tabs.addEventListener('pointerleave', release);

        // Swallow the click that ends a drag so it doesn't switch tabs.
        tabs.addEventListener(
          'click',
          (e) => {
            if (dragged) {
              e.preventDefault();
              e.stopPropagation();
            }
          },
          true
        );
      }

      setActive(index) {
        this.tabs.forEach((tab, i) => {
          const active = i === index;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        // Keep the active tab visible in the scrolling tab strip.
        const active = this.tabs[index];
        if (this.tabsEl && active) {
          const left = active.offsetLeft - this.tabsEl.clientWidth / 2 + active.offsetWidth / 2;
          this.tabsEl.scrollTo({ left: left, behavior: 'smooth' });
        }
      }

      disconnectedCallback() {
        if (this.carousel && this.carousel.slider) {
          this.carousel.slider.destroy(true, true);
        }
      }
    }
  );
}
